import { createContext, useContext, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import {
  ActiveTimerBanner,
  PersonalKpiStrip,
  UpcomingAbsencesCard,
} from "@/pages/personal/hub/PersonalHubWidgets";
import {
  usePersonalSelfData,
  type PersonalSelfData,
} from "@/pages/personal/hub/usePersonalSelfData";

interface PersonalSelfState {
  data: PersonalSelfData;
  loading: boolean;
}

const HomePersonalDataContext = createContext<PersonalSelfState | null>(null);

export function HomePersonalDataProvider({
  login,
  children,
}: {
  login: string;
  children: ReactNode;
}) {
  const state = usePersonalSelfData(login);
  return (
    <HomePersonalDataContext.Provider value={state}>{children}</HomePersonalDataContext.Provider>
  );
}

function useHomePersonalData(): PersonalSelfState {
  const state = useContext(HomePersonalDataContext);
  if (!state) {
    return {
      data: { account: null, month: null, absences: [], timer: null },
      loading: false,
    };
  }
  return state;
}

export function HomePersonalOverviewTile() {
  const { data, loading } = useHomePersonalData();
  return (
    <div className="space-y-4">
      <ActiveTimerBanner timer={data.timer} />
      <Card
        title="Mein Überblick"
        description="Aktueller Stand – Details in „Mein Bereich“."
        actions={
          <Link
            to="/personal/me"
            className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
          >
            Mein Bereich
          </Link>
        }
        bodyClassName="!p-4"
      >
        <PersonalKpiStrip data={data} loading={loading} />
      </Card>
    </div>
  );
}

export function HomePersonalAbsencesTile() {
  const { data, loading } = useHomePersonalData();
  return <UpcomingAbsencesCard absences={data.absences} loading={loading} />;
}
